import * as React       from 'react';
import { useNavigate }  from 'react-router-dom';
// import Backdrop         from '@mui/material/Backdrop';
// import CircularProgress from '@mui/material/CircularProgress';
import Stack            from "@mui/material/Stack";
import Menu             from '@mui/material/Menu';
import MenuItem         from '@mui/material/MenuItem';
import Dialog           from '@mui/material/Dialog';
import MoreVert         from '@mui/icons-material/MoreVert';
// import Tooltip           from '@mui/material/Tooltip';
// import LockIcon          from '@mui/icons-material/Lock';
import {
    DataGrid,
    GridActionsCellItem,
    GridCallbackDetails,
    GridColumns,
    GridEventListener,
    GridRowParams,
    MuiEvent
}                           from "@mui/x-data-grid";
import {
    UseQueryResult,
    useQuery,
    useQueryClient
}                           from 'react-query';
import IClass               from "models/IClass";
import DeleteClass          from 'ui/components/classes-components/DeleteClass';
import EditClass            from 'ui/components/classes-components/EditClass';
import ClassFlow            from 'ui/components/classes-components/ClassFlow';
import PopupWithCloseButton from 'ui/components/classes-components/PopupWithCloseButton';
import { HttpClient }       from 'apiclient/ApiClient';
import {
    CLASSES_TABLE_CLASS_NAME,
    CLASSES_TABLE_NO_CLASSES,
    CLASSES_TABLE_NUM,
    CLASSES_TABLE_GRADE,
    CLASSES_TABLE_DELETE_CLASS,
    CLASSES_TABLE_EDIT_CLASS,
    CLASSES_TABLE_ADD_STUDENTS,
    //CLASSES_TABLE_SHOW_QR_CODES,
    //QR_CODES_POPUP_HEADER,
    DELETE_CLASS_POPUP_HEADER,
    EDIT_CLASS_POPUP_HEADER,
}                           from 'config/config';
import { useProgramCatalog, gradeLabel } from 'hooks/useProgramCatalog';
import { Typography } from '@mui/material';


export default function ClassesTable() {
    const catalog = useProgramCatalog();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [selectedClass, setSelectedClass] = React.useState('');
    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupElement, setPopupElement] = React.useState<null | JSX.Element>(null);
    const [popupTitle, setPopupTitle] = React.useState('');
    const [menuRef, setMenuRef] = React.useState<null | HTMLElement>(null);
    const [flowOpen, setFlowOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const httpClient = HttpClient();
    const queryClient = useQueryClient();
    const classesGet: UseQueryResult<IClass[]> = useQuery(
        'classes',  async () => await httpClient.get('/school/classes'),
        {onSettled: ()=>setIsLoading(false)}
    );
    const invalidate = () => {
        queryClient.invalidateQueries('classes');
    };
    React.useEffect(()=>{
        if (classesGet.isLoading) {
            setIsLoading(true);
        }
    },[classesGet.isLoading])
    const navigate = useNavigate();
    const openClassMenu=(classId: string, ref: HTMLButtonElement)=>{
        setSelectedClass(classId);
        setMenuRef(ref);
        setMenuOpen(true);
    };
    const handleRowDoubleClick: GridEventListener<'rowDoubleClick'> = (
        params: GridRowParams,
        event: MuiEvent<React.MouseEvent<HTMLElement>>,
        details: GridCallbackDetails
    ) => {
        navigate(`/classes/${params.id}`)
    }
    const closePopup = () => {
        setPopupOpen(false);
        setPopupTitle('');
        setPopupElement(null);
    };
    const data_columns: GridColumns = [
        {   field: 'id',
            headerName: 'ID',
            editable: false,
            hide: true
        },
        {   field: 'num',
            headerName: CLASSES_TABLE_NUM,
            width: 56,
            editable: false,
            disableColumnMenu: true,
            hideSortIcons: true,
            align: 'center',
            headerAlign: 'center',
            cellClassName: 'NoPadding',
            headerClassName: 'NoPadding'
        },
        {   field: 'name',
            headerName: CLASSES_TABLE_CLASS_NAME,
            width: 220,
            editable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>{params.value}</Typography>
                    {/* Don't show lock icon as there is no support for mixed editable and read-only classes per organization */}
                    {/* {params.row.source !== 'teacher' && (
                        <Tooltip title="This class is read-only">
                            <LockIcon fontSize="small" sx={{ color: 'gray' }} />
                        </Tooltip>
                    )} */}
                </Stack>
            )
            }
        },
        {   field: 'grade',
            headerName: CLASSES_TABLE_GRADE,
            minWidth: 200,
            flex: 1,
            editable: false,
            disableColumnMenu: true
        },
        {   field: 'actions',
            type: 'actions',
            flex: 0.25,
            minWidth: 60,
            align: 'right',
            cellClassName: 'ActionsCellRight',
            hideSortIcons: true,
            disableColumnMenu: true,
            getActions: (params: GridRowParams) => {
                if (params.row.source !== 'teacher') {
                    return []; // Убираем действия для классов, которые нельзя редактировать
                }
                return [
                    <GridActionsCellItem
                        sx={{ mx: 0 }}
                        icon={<MoreVert />}
                        onClick={(e) => {
                            openClassMenu(params.id.toString(), e.currentTarget);
                        }}
                        label="Actions"
                    />
                ];
            } 
        }
    ];
    const data = React.useMemo(()=>{
        if (classesGet.data) {
            return classesGet.data;
        } else {
            return [];
        }
    },[classesGet.data])
    return(
        <>
            <DataGrid
                autoHeight
                sortingOrder={['desc', 'asc']}
                disableSelectionOnClick
                columns={data_columns}
                onRowClick={handleRowDoubleClick}
                rows={(data.sort(
                    (a, b)=>(a.name).localeCompare(b.name)
                ).map((cl, idx)=>{
                    return {
                        id: cl.id,
                        num: idx+1,
                        name: cl.name,
                        // Grade ids resolve to labels via the catalog; legacy classes
                        // stored the label (or free text) itself — shown verbatim.
                        grade: gradeLabel(catalog, cl.number),
                        source: cl.source,
                    }
                }))}
                hideFooter={true}
                rowHeight={56}
                headerHeight={70}
                components={{
                    NoRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                            {CLASSES_TABLE_NO_CLASSES}
                        </Stack>
                    )
                }}
                componentsProps={{
                    row: {
                        style: {fontSize: 16, cursor: 'pointer'}
                    }
                }}  
                loading={isLoading}
            />
            <Menu
                id="basic-menu"
                anchorEl={menuRef}
                open={menuOpen}
                onClose={()=>{
                    setMenuOpen(false);
                }}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={()=>{
                    setMenuOpen(false);
                    setPopupTitle(DELETE_CLASS_POPUP_HEADER);
                    const cl = classesGet.data?.find(cl=>cl.id === selectedClass);
                    setPopupElement(
                        <DeleteClass
                            closePopup={closePopup}
                            classId={selectedClass}
                            className={cl?.name || ''}
                            invalidate={invalidate}
                        />
                    )
                    setPopupOpen(true);
                }}>
                    {CLASSES_TABLE_DELETE_CLASS}
                </MenuItem>
                <MenuItem onClick={()=>{
                    setMenuOpen(false);
                    setPopupTitle(EDIT_CLASS_POPUP_HEADER);
                    setPopupElement(
                        <EditClass
                            closePopup={closePopup}
                            classId={selectedClass}
                            classData={data.filter(cl=>cl.id===selectedClass)[0]}
                            invalidate={invalidate}
                        />
                    );
                    setPopupOpen(true);
                }}>
                    {CLASSES_TABLE_EDIT_CLASS}
                </MenuItem>
                <MenuItem onClick={()=>{
                    setMenuOpen(false);
                    setFlowOpen(true);
                }}>
                    {CLASSES_TABLE_ADD_STUDENTS}
                </MenuItem>
            </Menu>
            <Dialog
                open={popupOpen}
                onClose={closePopup}
            >
                <PopupWithCloseButton
                    closePopap={closePopup}
                    title={popupTitle}
                >
                    {popupElement || <></>}
                </PopupWithCloseButton>
            </Dialog>
            <ClassFlow
                open={flowOpen}
                initialStep="add"
                classId={selectedClass}
                grade={classesGet.data?.find(cl => cl.id === selectedClass)?.number}
                onClose={() => setFlowOpen(false)}
                onFinish={() => { setFlowOpen(false); queryClient.invalidateQueries('classes'); }}
            />
            {/* <Backdrop open={isLoading} sx={{ zIndex: 2000, backdropFilter: 'brightness(80%)', backgroundColor: 'transparent' }}>
                <CircularProgress color="primary" />
            </Backdrop> */}
        </>
    );
};